/* eslint-disable */
import { useState, useMemo } from "react";
import { saveToStorage, loadFromStorage, useToast } from "./Auth";
import { saveAnalysisToCloud } from "./SupabaseSync";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — MATCH ANALYSIS
   Coaches log real stats after each game. Tag key moments.
   AI interprets numbers you actually recorded — not estimates.
═══════════════════════════════════════════════════════════ */

const MA_CSS = `
.ma-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); min-height:100%; }

/* HERO */
.ma-hero { background:linear-gradient(135deg,var(--ink),var(--teal2)); border-bottom:3px solid var(--mustard); padding:1.5rem 1.8rem; position:relative; overflow:hidden; }
.ma-hero::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 38px,rgba(255,255,255,0.02) 38px,rgba(255,255,255,0.02) 39px); pointer-events:none; }
.ma-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.ma-hero-title { font-family:'Bebas Neue',sans-serif; font-size:1.8rem; letter-spacing:3px; color:var(--white); line-height:1; }
.ma-hero-sub { font-size:0.72rem; color:rgba(255,255,255,0.5); font-style:italic; font-weight:300; margin-top:0.2rem; max-width:500px; line-height:1.65; }
.ma-stat-strip { display:flex; gap:0; position:relative; z-index:1; margin-top:1rem; }
.ma-stat { flex:1; text-align:center; padding:0.6rem 0.5rem; border-right:1px solid rgba(255,255,255,0.1); }
.ma-stat:last-child { border-right:none; }
.ma-stat-n { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:2px; color:var(--mustard); line-height:1; }
.ma-stat-l { font-size:0.52rem; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.35); margin-top:0.1rem; }

/* TABS */
.ma-tabs { display:flex; background:var(--white); border-bottom:2px solid var(--rule); overflow-x:auto; }
.ma-tab { padding:0.85rem 1.3rem; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; color:var(--muted); border-bottom:3px solid transparent; margin-bottom:-2px; white-space:nowrap; transition:all 0.2s; }
.ma-tab:hover { color:var(--teal); }
.ma-tab.on { color:var(--teal); border-bottom-color:var(--teal); }

/* CONTENT */
.ma-content { padding:1.2rem 1.5rem; max-width:960px; }

/* PANEL */
.ma-panel { background:var(--white); border:1.5px solid var(--rule); margin-bottom:1.2rem; }
.ma-panel-head { padding:0.85rem 1.1rem; border-bottom:1.5px solid var(--rule); display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:0.5rem; }
.ma-panel-title { font-family:'Bebas Neue',sans-serif; font-size:0.92rem; letter-spacing:2.5px; color:var(--ink); }
.ma-panel-body { padding:1.1rem; }
.ma-chip { font-size:0.56rem; font-weight:800; padding:0.12rem 0.45rem; letter-spacing:2px; text-transform:uppercase; }
.ma-chip.teal { background:var(--teal); color:var(--white); }
.ma-chip.mustard { background:var(--mustard); color:var(--ink); }

/* FORM */
.ma-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.85rem; }
.ma-form-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:0.85rem; }
.ma-label { display:block; font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.35rem; margin-top:0.75rem; }
.ma-label:first-child { margin-top:0; }
.ma-hint { font-size:0.55rem; font-weight:400; letter-spacing:0; text-transform:none; color:var(--muted); font-style:italic; margin-left:0.4rem; }
.ma-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.6rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; transition:border-color 0.2s; }
.ma-input:focus { border-bottom-color:var(--teal); }
.ma-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.6rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; cursor:pointer; }
.ma-textarea { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.65rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:300; outline:none; resize:vertical; min-height:90px; font-style:italic; }
.ma-textarea:focus { border-bottom-color:var(--teal); }

/* SLIDERS */
.ma-slider-row { display:flex; align-items:center; gap:0.75rem; padding:0.6rem 0; border-bottom:1px solid var(--rule2); }
.ma-slider-row:last-child { border-bottom:none; }
.ma-slider-label { font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:700; color:var(--ink); width:165px; flex-shrink:0; }
.ma-slider-sub { font-size:0.6rem; color:var(--muted); font-weight:300; display:block; margin-top:0.06rem; }
.ma-slider { flex:1; height:5px; border-radius:3px; outline:none; cursor:pointer; accent-color:var(--teal); }
.ma-slider-val { font-family:'Bebas Neue',sans-serif; font-size:1.1rem; letter-spacing:1px; width:44px; text-align:right; flex-shrink:0; }
.ma-bm { font-size:0.56rem; font-weight:800; padding:0.08rem 0.38rem; letter-spacing:1px; text-transform:uppercase; flex-shrink:0; }
.ma-bm.good { background:#dcfce7; color:#16a34a; }
.ma-bm.ok { background:var(--mustard3); color:var(--mustard2); }
.ma-bm.low { background:var(--darkred3); color:var(--darkred); }

/* MOMENTS */
.ma-moment-row { display:flex; gap:0.6rem; margin-bottom:0.55rem; align-items:flex-start; }
.ma-moment-time { background:var(--ink); color:var(--mustard); font-family:'Bebas Neue',sans-serif; font-size:0.85rem; letter-spacing:1px; padding:0.18rem 0.5rem; flex-shrink:0; min-width:44px; text-align:center; margin-top:0.15rem; }
.ma-moment-type { font-size:0.56rem; font-weight:800; padding:0.1rem 0.4rem; letter-spacing:1.5px; text-transform:uppercase; flex-shrink:0; margin-top:0.2rem; }
.ma-moment-type.press { background:var(--teal4); color:var(--teal); }
.ma-moment-type.goal { background:#dcfce7; color:#16a34a; }
.ma-moment-type.chance { background:var(--mustard3); color:var(--mustard2); }
.ma-moment-type.shape { background:#dbeafe; color:#1d4ed8; }
.ma-moment-type.concern { background:var(--darkred3); color:var(--darkred); }
.ma-moment-desc { font-size:0.78rem; color:var(--ink2); font-weight:400; flex:1; line-height:1.55; padding-top:0.18rem; }
.ma-moment-rm { background:transparent; border:none; color:var(--rule); cursor:pointer; font-size:0.72rem; padding:0.15rem 0.3rem; }
.ma-moment-rm:hover { color:var(--darkred); }

/* FINDINGS */
.ma-finding { display:flex; align-items:flex-start; gap:0.65rem; padding:0.55rem 0; border-bottom:1px solid var(--rule2); }
.ma-finding:last-child { border-bottom:none; }
.ma-finding-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; margin-top:0.35rem; }
.ma-finding-text { font-size:0.8rem; color:var(--ink2); font-weight:400; flex:1; line-height:1.6; }

/* HISTORY */
.ma-history-row { display:flex; align-items:center; gap:0.75rem; padding:0.62rem 1rem; border-bottom:1px solid var(--rule2); cursor:pointer; transition:all 0.15s; }
.ma-history-row:hover { background:var(--teal4); }
.ma-history-row:last-child { border-bottom:none; }
.ma-res { width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:0.75rem; flex-shrink:0; }
.ma-res.W { background:#16a34a; color:white; }
.ma-res.D { background:var(--mustard); color:var(--ink); }
.ma-res.L { background:var(--darkred); color:white; }
.ma-res.Q { background:var(--cream2); color:var(--muted); border:1.5px solid var(--rule); font-size:0.62rem; }
.ma-press-val { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:1px; }

/* RESULT CARDS */
.ma-result-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:0.65rem; margin:1rem; }
.ma-result-card { background:var(--cream2); border:1.5px solid var(--rule); padding:0.8rem 0.9rem; text-align:center; position:relative; overflow:hidden; }
.ma-result-card::after { content:''; position:absolute; bottom:0; left:0; right:0; height:3px; }
.ma-result-card.green::after { background:#16a34a; }
.ma-result-card.teal::after { background:var(--teal); }
.ma-result-card.mustard::after { background:var(--mustard); }
.ma-rval { font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:2px; line-height:1; }
.ma-rlbl { font-size:0.56rem; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-top:0.2rem; }

/* AI */
.ma-ai-badge { display:inline-flex; align-items:center; gap:0.35rem; font-size:0.56rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; padding:0.12rem 0.48rem; background:linear-gradient(135deg,var(--teal2),var(--teal)); color:var(--white); }
.ma-ai-badge::before { content:'✦'; }
.ma-ai-out { background:var(--cream); border:1.5px solid var(--rule); border-top:3px solid var(--teal); padding:1rem 1.1rem; font-family:'DM Sans',sans-serif; font-size:0.8rem; line-height:1.75; color:var(--ink2); font-weight:300; white-space:pre-wrap; word-break:break-word; }
.ma-cursor { display:inline-block; width:2px; height:1em; background:var(--teal); margin-left:2px; animation:cur 0.7s infinite; vertical-align:text-bottom; }
@keyframes cur { 0%,100%{opacity:1} 50%{opacity:0} }
.ma-loading { display:flex; align-items:center; gap:0.45rem; padding:0.5rem 0; font-size:0.7rem; color:var(--muted); font-style:italic; }
.ma-dot { width:6px; height:6px; border-radius:50%; background:var(--teal); animation:d 0.8s ease-in-out infinite alternate; }
.ma-dot:nth-child(2) { animation-delay:0.2s; }
.ma-dot:nth-child(3) { animation-delay:0.4s; }
@keyframes d { from{opacity:0.3;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }

/* SUBMIT */
.ma-save-btn { padding:0.82rem 2rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.78rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.ma-save-btn:hover { background:var(--teal2); }
.ma-save-btn:disabled { opacity:0.45; cursor:not-allowed; }

/* EMPTY */
.ma-empty { text-align:center; padding:4rem 2rem; }
.ma-empty-icon { font-size:3rem; margin-bottom:1rem; }
.ma-empty-title { font-family:'Bebas Neue',sans-serif; font-size:1.6rem; letter-spacing:2px; margin-bottom:0.5rem; }
.ma-empty-sub { font-size:0.82rem; color:var(--muted); font-style:italic; font-weight:300; max-width:400px; margin:0 auto; line-height:1.75; }

@media(max-width:900px) {
  .ma-result-grid { grid-template-columns:1fr 1fr; }
  .ma-form-grid, .ma-form-grid-3 { grid-template-columns:1fr; }
  .ma-content { padding:0.85rem; }
  .ma-slider-label { width:130px; font-size:0.72rem; }
}
`;

/* ── HELPERS ── */
function bm(v, thresholds) {
  if (v <= 0) return null;
  for (const [min, label, cls] of thresholds) {
    if (v >= min) return {label, cls};
  }
  return null;
}
const pressBM  = v => bm(v, [[75,"Elite","good"],[65,"Good","ok"],[55,"Developing","ok"],[0,"Needs Work","low"]]);
const possBM   = v => bm(v, [[58,"Dominant","good"],[48,"Balanced","ok"],[0,"Under pressure","low"]]);
const passAccBM = v => bm(v, [[85,"Precise","good"],[78,"Solid","ok"],[0,"Needs work","low"]]);

function resultLetter(gf, ga) {
  if (gf==="" || ga==="" || gf===undefined) return "Q";
  const f = Number(gf), a = Number(ga);
  return f > a ? "W" : f < a ? "L" : "D";
}

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"2-digit"});
}

function deriveFindings(stats, moments) {
  const f = [];
  if (stats.pressEfficiency >= 75) f.push({text:`Press efficiency of ${stats.pressEfficiency}% is elite — among the best. A genuine team strength.`, c:"#16a34a"});
  else if (stats.pressEfficiency >= 65) f.push({text:`Press at ${stats.pressEfficiency}% is solid but has room to grow. Sharpen triggers to push toward 75%+.`, c:"var(--mustard)"});
  else if (stats.pressEfficiency > 0) f.push({text:`Press efficiency of ${stats.pressEfficiency}% needs attention — pressing but not winning the ball. Focus on coordinated triggers.`, c:"var(--darkred)"});

  if (stats.possession >= 58) f.push({text:`${stats.possession}% possession — strong control. Make sure it translates into clear chances.`, c:"#16a34a"});
  else if (stats.possession > 0 && stats.possession < 45) f.push({text:`Only ${stats.possession}% possession — the team was under sustained pressure. Address ball retention in the next session.`, c:"var(--mustard)"});

  if (stats.passAccuracy > 0 && stats.passAccuracy < 78) f.push({text:`Pass accuracy of ${stats.passAccuracy}% is below the 78% benchmark. Are players choosing difficult passes or being forced into them?`, c:"var(--mustard)"});

  if (stats.chancesCreated > 0 && stats.shotsOnTarget > 0) {
    const conv = Math.round((stats.shotsOnTarget/stats.chancesCreated)*100);
    if (conv < 40) f.push({text:`${conv}% of chances became shots on target (${stats.shotsOnTarget} from ${stats.chancesCreated}). Improve final decision-making — shoot or square?`, c:"var(--mustard)"});
  }

  const concerns = moments.filter(m => m.type === "concern");
  if (concerns.length > 0) f.push({text:`${concerns.length} coaching concern${concerns.length>1?"s":""} tagged — priority areas for the next training session.`, c:"var(--darkred)"});

  const pressWins = moments.filter(m => m.type === "press");
  if (pressWins.length >= 3) f.push({text:`${pressWins.length} high press wins tagged — the front players are working hard and triggers are firing. Maintain this in the next match.`, c:"#16a34a"});

  return f;
}

const MOMENT_TYPES = [
  {id:"goal",    label:"Goal"},
  {id:"press",   label:"Press Win"},
  {id:"chance",  label:"Chance"},
  {id:"shape",   label:"Shape"},
  {id:"concern", label:"Concern"},
];

/* ══════════════════════════════════════
   COMPONENT
══════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   CV ANALYSIS TAB
   Accepts processor.py JSON output, sends to CV server,
   displays Claude tactical report.
═══════════════════════════════════════════════════════════ */
function CVAnalysisTab({
  cvFile, setCvFile, cvData, setCvData,
  cvCoachNotes, setCvCoachNotes, cvMatchContext, setCvMatchContext,
  cvAnalysing, setCvAnalysing, cvReport, setCvReport,
  cvError, setCvError, cvServerUrl, setCvServerUrl,
}) {
  const [showServerConfig, setShowServerConfig] = useState(false);
  const [serverStatus, setServerStatus] = useState(null); // null | "ok" | "error"

  const handleFileLoad = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCvFile(file);
    setCvData(null);
    setCvReport(null);
    setCvError("");
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        if (!data.summary || !data.events) {
          setCvError("This file doesn't look like processor.py output. Make sure you upload the _data.json file.");
          return;
        }
        setCvData(data);
      } catch {
        setCvError("Invalid JSON file. Upload the _data.json file from the processor.");
      }
    };
    reader.readAsText(file);
  };

  const checkServer = async () => {
    try {
      const r = await fetch(`${cvServerUrl}/health`, {signal: AbortSignal.timeout(3000)});
      setServerStatus(r.ok ? "ok" : "error");
    } catch {
      setServerStatus("error");
    }
  };

  const runAnalysis = async () => {
    if (!cvData) return;
    setCvAnalysing(true);
    setCvError("");
    setCvReport(null);
    try {
      const r = await fetch(`${cvServerUrl}/analyse`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          data: cvData,
          coach_notes: cvCoachNotes,
          match_context: cvMatchContext,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(()=>({error:`Server error ${r.status}`}));
        throw new Error(err.error || `Server error ${r.status}`);
      }
      const result = await r.json();
      setCvReport(result);
    } catch (e) {
      setCvError(`Analysis failed: ${e.message}. Make sure the CV server is running (python server.py).`);
    } finally {
      setCvAnalysing(false);
    }
  };

  const s = cvData?.summary || {};
  const m = cvData?.meta || {};

  return (
    <div>
      {/* ── HOW IT WORKS BANNER ── */}
      <div style={{background:"var(--ink)",borderLeft:"4px solid var(--mustard)",padding:"1rem 1.2rem",marginBottom:"1.2rem"}}>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:"var(--mustard)",marginBottom:"0.4rem"}}>
          Computer Vision Match Analysis
        </div>
        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"rgba(255,255,255,0.65)",fontWeight:300,lineHeight:1.7}}>
          1. Run <code style={{background:"rgba(255,255,255,0.1)",padding:"0.1rem 0.35rem",fontFamily:"monospace",fontSize:"0.68rem"}}>processor.py</code> on your match footage on your laptop — outputs a <code style={{background:"rgba(255,255,255,0.1)",padding:"0.1rem 0.35rem",fontFamily:"monospace",fontSize:"0.68rem"}}>match_data.json</code> file.
          <br/>2. Upload that JSON here.
          <br/>3. Claude reads the player positions, pressing data and tactical events — and writes a coaching report.
        </div>
        <div style={{display:"flex",gap:"0.5rem",marginTop:"0.75rem",flexWrap:"wrap"}}>
          <code style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",padding:"0.3rem 0.65rem",fontFamily:"monospace",fontSize:"0.65rem",color:"rgba(255,255,255,0.8)"}}>
            pip install ultralytics opencv-python
          </code>
          <code style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",padding:"0.3rem 0.65rem",fontFamily:"monospace",fontSize:"0.65rem",color:"rgba(255,255,255,0.8)"}}>
            python processor.py --video match.mp4 --team-colour blue --opp-colour red
          </code>
        </div>
      </div>

      {/* ── SERVER CONFIG ── */}
      <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",marginBottom:"1.2rem"}}>
        <div style={{padding:"0.75rem 1rem",borderBottom:"1px solid var(--rule2)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.6rem"}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:serverStatus==="ok"?"#16a34a":serverStatus==="error"?"var(--darkred)":"var(--muted)"}}/>
            <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:700,color:"var(--ink)"}}>
              CV Server: {cvServerUrl}
            </span>
            {serverStatus==="ok"&&<span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"#16a34a",fontWeight:700}}>● Connected</span>}
            {serverStatus==="error"&&<span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--darkred)",fontWeight:700}}>✕ Not reachable</span>}
          </div>
          <div style={{display:"flex",gap:"0.4rem"}}>
            <button onClick={checkServer} style={{padding:"0.28rem 0.65rem",background:"transparent",border:"1.5px solid var(--rule)",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:"0.6rem",letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer",color:"var(--muted)"}}>
              Test Connection
            </button>
            <button onClick={()=>setShowServerConfig(s=>!s)} style={{padding:"0.28rem 0.65rem",background:"transparent",border:"1.5px solid var(--rule)",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:"0.6rem",letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer",color:"var(--muted)"}}>
              Configure
            </button>
          </div>
        </div>
        {showServerConfig&&(
          <div style={{padding:"0.85rem 1rem",background:"var(--cream2)"}}>
            <label style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.35rem"}}>
              Server URL
            </label>
            <input
              value={cvServerUrl}
              onChange={e=>setCvServerUrl(e.target.value)}
              style={{width:"100%",background:"var(--white)",border:"1.5px solid var(--rule)",borderBottom:"2px solid var(--teal)",padding:"0.55rem 0.8rem",fontFamily:"monospace",fontSize:"0.82rem",outline:"none",color:"var(--ink)"}}
              placeholder="http://localhost:5050"
            />
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.4rem"}}>
              Run <code style={{fontFamily:"monospace"}}>python server.py</code> locally, or deploy to Railway/Render for a hosted URL.
            </div>
          </div>
        )}
      </div>

      {/* ── FILE UPLOAD ── */}
      <div className="ma-panel">
        <div className="ma-panel-head">
          <span className="ma-panel-title">Upload Processed Data</span>
          {cvData&&<span className="ma-chip teal">✓ Loaded</span>}
        </div>
        <div className="ma-panel-body">
          <label style={{display:"block",cursor:"pointer"}}>
            <div style={{border:`2px dashed ${cvData?"var(--teal)":"var(--rule)"}`,padding:"1.8rem",textAlign:"center",background:cvData?"var(--teal4)":"var(--cream2)",transition:"all 0.2s"}}>
              <div style={{fontSize:"2rem",marginBottom:"0.5rem"}}>{cvData?"✓":"📁"}</div>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:cvData?"var(--teal)":"var(--ink)",marginBottom:"0.25rem"}}>
                {cvData ? `Loaded: ${m.video||cvFile?.name}` : "Drop JSON file or click to browse"}
              </div>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic"}}>
                {cvData ? `${s.frames_analysed||0} frames · ${s.total_events||0} events · ${m.duration_mins||0}min` : "Upload the _data.json file from processor.py"}
              </div>
            </div>
            <input type="file" accept=".json" onChange={handleFileLoad} style={{display:"none"}}/>
          </label>

          {/* Quick summary if loaded */}
          {cvData&&(
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:"0.65rem",marginTop:"1rem"}}>
              {[
                {label:"Frames analysed", val:s.frames_analysed||0},
                {label:"Events detected", val:s.total_events||0},
                {label:"Press triggers",  val:(s.high_press_moments||[]).length},
                {label:"Shape breakdowns",val:(s.shape_breakdowns||[]).length},
                {label:"High line exp.",  val:(s.high_line_exposures||[]).length},
                {label:"Ball tracking",  val:`${((s.ball_detection_rate||0)*100).toFixed(0)}%`},
                {label:"Formation hint", val:s.dominant_formation||"—"},
                {label:"Avg pressing",   val:`${s.avg_pressing?.home?.toFixed(1)||"—"}`},
              ].map((item,i)=>(
                <div key={i} style={{background:"var(--cream2)",border:"1px solid var(--rule)",padding:"0.6rem 0.75rem"}}>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"1px",color:"var(--teal)"}}>{item.val}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:"var(--muted)",marginTop:"0.1rem"}}>{item.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── CONTEXT ── */}
      {cvData&&(
        <div className="ma-panel">
          <div className="ma-panel-head">
            <span className="ma-panel-title">Match Context</span>
            <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic"}}>Helps Claude give more specific analysis</span>
          </div>
          <div className="ma-panel-body">
            <label className="ma-label">Match Details</label>
            <input className="ma-input" placeholder="e.g. vs North United, home, league, lost 1-2" value={cvMatchContext} onChange={e=>setCvMatchContext(e.target.value)}/>
            <label className="ma-label">Coach Observations <span className="ma-hint">— what you noticed watching the match</span></label>
            <textarea className="ma-textarea" placeholder="e.g. We struggled to press in the second half. The CM kept getting caught on the wrong side. Their No.10 had too much space between our lines." value={cvCoachNotes} onChange={e=>setCvCoachNotes(e.target.value)}/>
          </div>
        </div>
      )}

      {/* ── RUN ANALYSIS ── */}
      {cvData&&(
        <button
          onClick={runAnalysis}
          disabled={cvAnalysing}
          style={{width:"100%",padding:"1rem",background:cvAnalysing?"var(--muted)":"var(--darkred)",color:"var(--white)",border:"none",fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"3px",cursor:cvAnalysing?"default":"pointer",marginBottom:"1.2rem",transition:"background 0.2s"}}>
          {cvAnalysing?"Analysing footage data with Claude...":"✦ Generate Tactical Report from CV Data →"}
        </button>
      )}

      {/* ── ERROR ── */}
      {cvError&&(
        <div style={{background:"rgba(139,42,42,0.1)",border:"1.5px solid var(--darkred)",borderLeft:"4px solid var(--darkred)",padding:"0.85rem 1rem",marginBottom:"1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--darkred)",lineHeight:1.65}}>
          ✕ {cvError}
        </div>
      )}

      {/* ── REPORT ── */}
      {cvReport&&(
        <div className="ma-panel">
          <div className="ma-panel-head">
            <span className="ma-panel-title">CV Tactical Report</span>
            <span className="ma-chip teal">CV Analysis</span>
          </div>
          <div className="ma-panel-body">
            {/* Key numbers row */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.5rem",marginBottom:"1.2rem"}}>
              {[
                {l:"Press triggers", v:(cvReport.summary?.high_press_moments||[]).length, c:"var(--teal)"},
                {l:"Shape breakdowns", v:(cvReport.summary?.shape_breakdowns||[]).length, c:"var(--mustard)"},
                {l:"High line exposed", v:(cvReport.summary?.high_line_exposures||[]).length, c:"var(--darkred)"},
              ].map((item,i)=>(
                <div key={i} style={{textAlign:"center",padding:"0.75rem",background:"var(--cream2)",border:"1.5px solid var(--rule)"}}>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.8rem",letterSpacing:"2px",color:item.c,lineHeight:1}}>{item.v}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",color:"var(--muted)",marginTop:"0.2rem"}}>{item.l}</div>
                </div>
              ))}
            </div>

            {/* Events timeline */}
            {(cvReport.summary?.high_press_moments||[]).length > 0 && (
              <div style={{marginBottom:"1.2rem"}}>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>Key Events Timeline</div>
                <div style={{maxHeight:180,overflowY:"auto",display:"flex",flexDirection:"column",gap:"0.25rem"}}>
                  {[...(cvReport.summary?.high_press_moments||[]), ...(cvReport.summary?.high_line_exposures||[])].sort((a,b)=>a.timestamp-b.timestamp).map((e,i)=>(
                    <div key={i} style={{display:"flex",gap:"0.6rem",alignItems:"flex-start",padding:"0.35rem 0.5rem",background:e.severity==="high"?"rgba(139,42,42,0.08)":"var(--cream2)",border:`1px solid ${e.severity==="high"?"rgba(139,42,42,0.25)":"var(--rule2)"}`}}>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.72rem",letterSpacing:"1px",color:"var(--muted)",flexShrink:0,minWidth:32}}>{e.timestamp_display}</span>
                      <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--ink)",flex:1,lineHeight:1.5}}>{e.type.replace(/_/g," ")} — {e.details}</span>
                      <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.55rem",fontWeight:800,color:e.severity==="high"?"var(--darkred)":"var(--mustard)",flexShrink:0,textTransform:"uppercase"}}>{e.severity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Claude report text */}
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",color:"var(--ink2)",lineHeight:1.85,whiteSpace:"pre-wrap",fontWeight:300}}>
              {cvReport.report}
            </div>
          </div>
        </div>
      )}

      {/* ── SETUP GUIDE (when no file) ── */}
      {!cvData&&!cvError&&(
        <div className="ma-panel">
          <div className="ma-panel-head">
            <span className="ma-panel-title">Setup Guide</span>
          </div>
          <div className="ma-panel-body">
            {[
              {n:1, title:"Install dependencies", cmd:"pip install ultralytics opencv-python numpy scipy"},
              {n:2, title:"Run the processor on your footage", cmd:"python processor.py --video match.mp4 --team-colour blue --opp-colour red"},
              {n:3, title:"Check available colours if unsure", cmd:"python processor.py --video match.mp4 --team-colour blue --opp-colour red --debug-colours"},
              {n:4, title:"Process a specific period (e.g. second half)", cmd:"python processor.py --video match.mp4 --team-colour blue --opp-colour red --start 45 --end 90"},
              {n:5, title:"Start the analysis server", cmd:"pip install flask anthropic && python server.py"},
              {n:6, title:"Upload the JSON file above", cmd:"— then click Generate Tactical Report"},
            ].map((step,i)=>(
              <div key={i} style={{display:"flex",gap:"0.85rem",padding:"0.75rem 0",borderBottom:"1px solid var(--rule2)"}}>
                <div style={{width:22,height:22,borderRadius:"50%",background:"var(--teal)",color:"var(--white)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.75rem",flexShrink:0,marginTop:"0.1rem"}}>{step.n}</div>
                <div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",fontWeight:700,color:"var(--ink)",marginBottom:"0.25rem"}}>{step.title}</div>
                  <code style={{display:"block",background:"var(--cream2)",border:"1px solid var(--rule)",padding:"0.3rem 0.6rem",fontFamily:"monospace",fontSize:"0.65rem",color:"var(--ink2)",wordBreak:"break-all"}}>{step.cmd}</code>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function VideoAnalysis() {
  const [tab, setTab] = useState("log");

  // CV Analysis state
  const [cvFile, setCvFile] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [cvCoachNotes, setCvCoachNotes] = useState("");
  const [cvMatchContext, setCvMatchContext] = useState("");
  const [cvAnalysing, setCvAnalysing] = useState(false);
  const [cvReport, setCvReport] = useState(null);
  const [cvError, setCvError] = useState("");
  const [cvServerUrl, setCvServerUrl] = useState(() => loadFromStorage("cv_server_url","http://localhost:5050") || "http://localhost:5050");
  const [analyses, setAnalyses] = useState(() => loadFromStorage("va_analyses", []));
  const [viewingId, setViewingId] = useState(null);
  const [aiOutput, setAiOutput] = useState("");
  const [aiStreaming, setAiStreaming] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const toast = useToast();

  // Match details
  const [match, setMatch] = useState({opponent:"",date:"",competition:"League",venue:"home",goalsFor:"",goalsAgainst:""});
  const setM = (k,v) => setMatch(p=>({...p,[k]:v}));

  // Stats
  const [stats, setStats] = useState({pressEfficiency:0,possession:0,passAccuracy:0,highPressWins:0,chancesCreated:0,shotsOnTarget:0,distanceCovered:"",cornersTaken:"",foulsCommitted:"",yellowCards:""});
  const setStat = (k,v) => setStats(p=>({...p,[k]:v}));

  // Moments
  const [moments, setMoments] = useState([]);
  const [nm, setNm] = useState({time:"",type:"press",desc:""});
  const [notes, setNotes] = useState("");

  const findings = useMemo(() => deriveFindings(stats, moments), [stats, moments]);
  const res = resultLetter(match.goalsFor, match.goalsAgainst);
  const avgPress = (() => {
    const a = analyses.filter(x=>x.pressEfficiency>0);
    return a.length ? Math.round(a.reduce((s,x)=>s+x.pressEfficiency,0)/a.length) : 0;
  })();
  const viewing = analyses.find(a => a.id === viewingId);

  /* ── SAVE ── */
  const save = async () => {
    if (!match.opponent) return;
    const record = {
      id: Date.now(),
      matchData: {...match},
      pressEfficiency:   Number(stats.pressEfficiency)||0,
      possessionPct:     Number(stats.possession)||0,
      passAccuracy:      Number(stats.passAccuracy)||0,
      highPressWins:     Number(stats.highPressWins)||0,
      chancesCreated:    Number(stats.chancesCreated)||0,
      shotsOnTarget:     Number(stats.shotsOnTarget)||0,
      distanceCovered:   stats.distanceCovered||0,
      pressingZones:     [],
      stats:             {...stats},
      moments:           [...moments],
      coachNotes:        notes,
      aiOutput:          "",
      savedAt: new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
    };
    const updated = [record, ...analyses];
    setAnalyses(updated);
    saveToStorage("va_analyses", updated);
    await saveAnalysisToCloud(record, match);
    setSaveStatus("saved");
    setTimeout(() => setSaveStatus(""), 2500);
    toast && toast(`Match analysis saved — vs ${match.opponent}`, "success");
    // Reset
    setMatch({opponent:"",date:"",competition:"League",venue:"home",goalsFor:"",goalsAgainst:""});
    setStats({pressEfficiency:0,possession:0,passAccuracy:0,highPressWins:0,chancesCreated:0,shotsOnTarget:0,distanceCovered:"",cornersTaken:"",foulsCommitted:"",yellowCards:""});
    setMoments([]); setNotes(""); setTab("history");
  };

  /* ── AI REPORT ── */
  const genAI = async (a) => {
    setAiStreaming(true); setAiOutput("");
    try {
      const body = {
        system:`You are a performance analyst. Write sharp, direct match reports. Be specific about the numbers. Use ## for section headers. No padding.`,
        messages:[{role:"user",content:`Match analysis report:

vs ${a.matchData?.opponent} | ${a.matchData?.competition||""} | ${a.matchData?.venue==="home"?"Home":"Away"}${a.matchData?.goalsFor!==undefined?` | ${a.matchData.goalsFor}–${a.matchData.goalsAgainst}`:""}

Stats:
- Press efficiency: ${a.pressEfficiency}% (65%=good, 75%=elite)
- Possession: ${a.possessionPct}%
- Pass accuracy: ${a.passAccuracy}%
- Press wins: ${a.highPressWins}
- Chances created: ${a.chancesCreated}
- Shots on target: ${a.shotsOnTarget}
${a.distanceCovered?`- Distance: ${a.distanceCovered}km`:""}
${a.stats?.cornersTaken?`- Corners: ${a.stats.cornersTaken}`:""}

Moments tagged:
${a.moments?.length>0 ? a.moments.map(m=>`- ${m.time}' [${m.type.toUpperCase()}] ${m.desc}`).join("\n") : "None"}

Coach notes: ${a.coachNotes||"None"}

Write: ## Executive Summary (2-3 sentences max), ## Key Patterns (what data shows tactically), ## 3 Priority Training Actions. Be direct and specific.`}],
        max_tokens:700, stream:true,
      };
      const r = await fetch("/api/claude",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      if (!r.ok) throw new Error(`${r.status}`);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let full = "";
      while(true) {
        const {done,value} = await reader.read();
        if(done) break;
        for(const line of dec.decode(value,{stream:true}).split("\n")) {
          if(!line.startsWith("data: ")) continue;
          const d = line.slice(6).trim();
          if(d==="[DONE]") continue;
          try { const delta=JSON.parse(d)?.delta?.text||""; if(delta){full+=delta;setAiOutput(full);} } catch{}
        }
      }
      // Save AI output to this analysis
      setAnalyses(prev => prev.map(x => x.id===a.id ? {...x,aiOutput:full} : x));
      const updated = analyses.map(x => x.id===a.id ? {...x,aiOutput:full} : x);
      saveToStorage("va_analyses", updated);
    } catch(e) { setAiOutput(`Error: ${e.message}`); }
    setAiStreaming(false);
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{MA_CSS}</style>
      <div className="ma-wrap">

        <div className="ma-hero">
          <div className="ma-hero-inner">
            <div>
              <div className="ma-hero-title">Match Analysis</div>
              <div className="ma-hero-sub">Log your real match stats after each game. Tag key moments. Get AI coaching reports based on numbers you actually recorded.</div>
            </div>
          </div>
          <div className="ma-stat-strip">
            <div className="ma-stat"><div className="ma-stat-n">{analyses.length}</div><div className="ma-stat-l">Matches Logged</div></div>
            <div className="ma-stat"><div className="ma-stat-n">{avgPress>0?`${avgPress}%`:"—"}</div><div className="ma-stat-l">Avg Press Eff.</div></div>
            <div className="ma-stat"><div className="ma-stat-n">{analyses.filter(a=>resultLetter(a.matchData?.goalsFor,a.matchData?.goalsAgainst)==="W").length}</div><div className="ma-stat-l">Wins</div></div>
            <div className="ma-stat"><div className="ma-stat-n">{analyses.reduce((s,a)=>s+(a.moments?.length||0),0)}</div><div className="ma-stat-l">Moments Tagged</div></div>
          </div>
        </div>

        <div className="ma-tabs">
          {[
            {id:"log",     l:"📋 Log Match Stats"},
            {id:"cv",      l:"🎥 CV Analysis"},
            {id:"history", l:`📊 History${analyses.length>0?` (${analyses.length})`:""}`},
          ].map(t=>(
            <div key={t.id} className={`ma-tab ${tab===t.id?"on":""}`} onClick={()=>setTab(t.id)}>{t.l}</div>
          ))}
        </div>

        <div className="ma-content">

          {/* ══ LOG ══ */}
          {tab==="log"&&(
            <>
              {/* Match details */}
              <div className="ma-panel">
                <div className="ma-panel-head">
                  <div className="ma-panel-title">Match Details</div>
                  {res!=="Q"&&<div className={`ma-res ${res}`}>{res}</div>}
                </div>
                <div className="ma-panel-body">
                  <div className="ma-form-grid">
                    <div>
                      <label className="ma-label">Opponent *</label>
                      <input className="ma-input" placeholder="e.g. North United" value={match.opponent} onChange={e=>setM("opponent",e.target.value)} autoFocus/>
                    </div>
                    <div>
                      <label className="ma-label">Date</label>
                      <input className="ma-input" type="date" value={match.date} onChange={e=>setM("date",e.target.value)}/>
                    </div>
                    <div>
                      <label className="ma-label">Competition</label>
                      <select className="ma-select" value={match.competition} onChange={e=>setM("competition",e.target.value)}>
                        {["League","League Cup","FA Cup","Friendly","Playoff","Pre-season"].map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="ma-label">Venue</label>
                      <select className="ma-select" value={match.venue} onChange={e=>setM("venue",e.target.value)}>
                        <option value="home">Home</option>
                        <option value="away">Away</option>
                      </select>
                    </div>
                    <div>
                      <label className="ma-label">Goals For</label>
                      <input className="ma-input" type="number" min="0" placeholder="0" value={match.goalsFor} onChange={e=>setM("goalsFor",e.target.value)}/>
                    </div>
                    <div>
                      <label className="ma-label">Goals Against</label>
                      <input className="ma-input" type="number" min="0" placeholder="0" value={match.goalsAgainst} onChange={e=>setM("goalsAgainst",e.target.value)}/>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance stats */}
              <div className="ma-panel">
                <div className="ma-panel-head">
                  <div className="ma-panel-title">Performance Stats</div>
                  <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic"}}>Enter what you have — leave others at 0</span>
                </div>
                <div className="ma-panel-body">
                  {[
                    {k:"pressEfficiency",label:"Pressing Efficiency",sub:"% of presses that won the ball",bm:pressBM,max:100,unit:"%"},
                    {k:"possession",     label:"Possession",          sub:"% ball possession",             bm:possBM,  max:100,unit:"%"},
                    {k:"passAccuracy",   label:"Pass Accuracy",       sub:"% of passes completed",         bm:passAccBM,max:100,unit:"%"},
                  ].map(({k,label,sub,bm:getBm,max,unit})=>{
                    const val = Number(stats[k])||0;
                    const mark = val>0?getBm(val):null;
                    return(
                      <div key={k} className="ma-slider-row">
                        <div className="ma-slider-label">
                          {label}<span className="ma-slider-sub">{sub}</span>
                        </div>
                        <input type="range" className="ma-slider" min="0" max={max} value={val} onChange={e=>setStat(k,e.target.value)}/>
                        <div className="ma-slider-val" style={{color:val===0?"var(--muted)":val>=75?"#16a34a":val>=60?"var(--mustard)":"var(--darkred)"}}>
                          {val===0?"—":val}
                        </div>
                        <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)",width:16,flexShrink:0}}>{val>0?unit:""}</span>
                        {mark&&<span className={`ma-bm ${mark.cls}`}>{mark.label}</span>}
                      </div>
                    );
                  })}

                  <div className="ma-form-grid-3" style={{marginTop:"0.85rem"}}>
                    {[
                      {k:"highPressWins", label:"Press Wins",      hint:"Won ball high up pitch"},
                      {k:"chancesCreated",label:"Chances Created",  hint:"Clear opportunities"},
                      {k:"shotsOnTarget", label:"Shots on Target",  hint:""},
                      {k:"cornersTaken",  label:"Corners",          hint:""},
                      {k:"foulsCommitted",label:"Fouls",            hint:""},
                      {k:"yellowCards",   label:"Yellow Cards",     hint:""},
                    ].map(({k,label,hint})=>(
                      <div key={k}>
                        <label className="ma-label">{label}{hint&&<span className="ma-hint">{hint}</span>}</label>
                        <input className="ma-input" type="number" min="0" placeholder="0" value={stats[k]||""} onChange={e=>setStat(k,e.target.value)}/>
                      </div>
                    ))}
                  </div>

                  <div style={{marginTop:"0.5rem"}}>
                    <label className="ma-label">Distance Covered (km) <span className="ma-hint">from GPS if available</span></label>
                    <input className="ma-input" type="number" step="0.1" min="0" placeholder="e.g. 104.5" value={stats.distanceCovered||""} onChange={e=>setStat("distanceCovered",e.target.value)} style={{maxWidth:200}}/>
                  </div>
                </div>
              </div>

              {/* Live findings */}
              {findings.length>0&&(
                <div className="ma-panel" style={{borderTop:"3px solid var(--teal)"}}>
                  <div className="ma-panel-head">
                    <div className="ma-panel-title">Automatic Findings</div>
                    <span className="ma-chip teal">Live</span>
                  </div>
                  <div className="ma-panel-body">
                    {findings.map((f,i)=>(
                      <div key={i} className="ma-finding">
                        <div className="ma-finding-dot" style={{background:f.c}}/>
                        <div className="ma-finding-text">{f.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key moments */}
              <div className="ma-panel">
                <div className="ma-panel-head">
                  <div className="ma-panel-title">Key Moments <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:400,color:"var(--muted)"}}> — optional</span></div>
                  <span className="ma-chip mustard">{moments.length} tagged</span>
                </div>
                <div className="ma-panel-body">
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic",marginBottom:"0.85rem",lineHeight:1.6}}>
                    Tag moments worth addressing in training — goals, press wins, shape breakdowns, coaching concerns.
                  </div>
                  {moments.map(m=>(
                    <div key={m.id} className="ma-moment-row">
                      <div className="ma-moment-time">{m.time||"—"}'</div>
                      <span className={`ma-moment-type ${m.type}`}>{MOMENT_TYPES.find(t=>t.id===m.type)?.label}</span>
                      <div className="ma-moment-desc">{m.desc}</div>
                      <button className="ma-moment-rm" onClick={()=>setMoments(prev=>prev.filter(x=>x.id!==m.id))}>✕</button>
                    </div>
                  ))}
                  <div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap",marginTop:"0.75rem",alignItems:"flex-end"}}>
                    <div>
                      <label className="ma-label" style={{marginTop:0}}>Minute</label>
                      <input className="ma-input" type="number" min="0" max="120" placeholder="45" value={nm.time} onChange={e=>setNm(p=>({...p,time:e.target.value}))} style={{width:80}}/>
                    </div>
                    <div>
                      <label className="ma-label" style={{marginTop:0}}>Type</label>
                      <select className="ma-select" value={nm.type} onChange={e=>setNm(p=>({...p,type:e.target.value}))} style={{width:140}}>
                        {MOMENT_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                    </div>
                    <div style={{flex:1,minWidth:180}}>
                      <label className="ma-label" style={{marginTop:0}}>Description</label>
                      <input className="ma-input" placeholder="e.g. High press wins ball, leads to goal" value={nm.desc} onChange={e=>setNm(p=>({...p,desc:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&nm.desc.trim()&&(setMoments(p=>[...p,{...nm,id:Date.now()}]),setNm({time:"",type:"press",desc:""}))}/>
                    </div>
                    <button onClick={()=>{if(!nm.desc.trim())return;setMoments(p=>[...p,{...nm,id:Date.now()}]);setNm({time:"",type:"press",desc:""}),""}}
                      disabled={!nm.desc.trim()}
                      style={{padding:"0.6rem 1rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.68rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer",flexShrink:0,alignSelf:"flex-end",opacity:nm.desc.trim()?1:0.5}}>
                      + Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Coach notes */}
              <div className="ma-panel">
                <div className="ma-panel-head">
                  <div className="ma-panel-title">Coach Notes <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:400,color:"var(--muted)"}}> — optional</span></div>
                </div>
                <div className="ma-panel-body">
                  <textarea className="ma-textarea" style={{minHeight:90}}
                    placeholder="General observations, things to address in training, individual player notes, what worked and what didn't..."
                    value={notes} onChange={e=>setNotes(e.target.value)}/>
                </div>
              </div>

              <div style={{display:"flex",gap:"0.75rem",alignItems:"center",flexWrap:"wrap"}}>
                <button className="ma-save-btn" onClick={save} disabled={!match.opponent}>
                  {saveStatus==="saved"?"✓ Saved to History":"Save Match Analysis"}
                </button>
                <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic"}}>
                  {match.opponent?"Feeds into Season Tracker and AI Analyst":"Enter opponent name to save"}
                </span>
              </div>
            </>
          )}

          {/* ══ HISTORY ══ */}
          {/* ══ CV ANALYSIS ══ */}
          {tab==="cv"&&(
            <CVAnalysisTab
              cvFile={cvFile} setCvFile={setCvFile}
              cvData={cvData} setCvData={setCvData}
              cvCoachNotes={cvCoachNotes} setCvCoachNotes={setCvCoachNotes}
              cvMatchContext={cvMatchContext} setCvMatchContext={setCvMatchContext}
              cvAnalysing={cvAnalysing} setCvAnalysing={setCvAnalysing}
              cvReport={cvReport} setCvReport={setCvReport}
              cvError={cvError} setCvError={setCvError}
              cvServerUrl={cvServerUrl} setCvServerUrl={v=>{setCvServerUrl(v);saveToStorage("cv_server_url",v);}}
            />
          )}

          {/* ══ HISTORY ══ */}
          {tab==="history"&&(
            analyses.length===0?(
              <div className="ma-empty">
                <div className="ma-empty-icon">📋</div>
                <div className="ma-empty-title">No Matches Logged Yet</div>
                <div className="ma-empty-sub">Log your first match stats — the more you log, the more useful the Season Tracker and AI Analyst become.</div>
                <button className="ma-save-btn" style={{marginTop:"1.5rem"}} onClick={()=>setTab("log")}>Log First Match →</button>
              </div>
            ):(
              <>
                {/* Viewing analysis */}
                {viewingId&&viewing&&(
                  <div className="ma-panel" style={{borderTop:"3px solid var(--teal)"}}>
                    <div className="ma-panel-head">
                      <div style={{flex:1}}>
                        <div className="ma-panel-title">vs {viewing.matchData?.opponent}</div>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",marginTop:"0.1rem"}}>
                          {fmtDate(viewing.matchData?.date)}{viewing.matchData?.competition?` · ${viewing.matchData.competition}`:""}{viewing.matchData?.venue?` · ${viewing.matchData.venue==="home"?"Home":"Away"}`:""}{viewing.matchData?.goalsFor!==undefined?` · ${viewing.matchData.goalsFor}–${viewing.matchData.goalsAgainst}`:""}
                        </div>
                      </div>
                      <button onClick={()=>{setViewingId(null);setAiOutput("");}} style={{padding:"0.3rem 0.72rem",background:"transparent",border:"1.5px solid var(--rule)",color:"var(--muted)",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:"0.62rem",letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer"}}>Close ✕</button>
                    </div>

                    {/* Stats */}
                    <div className="ma-result-grid">
                      {[
                        {l:"Press Eff",  v:viewing.pressEfficiency,   u:"%", c:viewing.pressEfficiency>=75?"#16a34a":viewing.pressEfficiency>=65?"var(--teal)":"var(--mustard)", cls:"teal"},
                        {l:"Possession", v:viewing.possessionPct,     u:"%", c:"var(--teal)", cls:"teal"},
                        {l:"Pass Acc",   v:viewing.passAccuracy,      u:"%", c:viewing.passAccuracy>=85?"#16a34a":"var(--mustard)", cls:"green"},
                        {l:"Chances",    v:viewing.chancesCreated,    u:"",  c:"var(--ink)", cls:"mustard"},
                        {l:"Press Wins", v:viewing.highPressWins,     u:"",  c:"var(--teal)", cls:"teal"},
                        {l:"On Target",  v:viewing.shotsOnTarget,     u:"",  c:"var(--ink)", cls:"mustard"},
                      ].filter(s=>s.v>0).map((s,i)=>(
                        <div key={i} className={`ma-result-card ${s.cls}`}>
                          <div className="ma-rval" style={{color:s.c}}>{s.v}{s.u}</div>
                          <div className="ma-rlbl">{s.l}</div>
                        </div>
                      ))}
                    </div>

                    {/* Moments */}
                    {viewing.moments?.length>0&&(
                      <div style={{padding:"0 1rem 0.5rem"}}>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.82rem",letterSpacing:"2px",color:"var(--muted)",marginBottom:"0.6rem",borderBottom:"1px solid var(--rule2)",paddingBottom:"0.35rem"}}>Key Moments</div>
                        {viewing.moments.map((m,i)=>(
                          <div key={i} className="ma-moment-row">
                            <div className="ma-moment-time">{m.time||"—"}'</div>
                            <span className={`ma-moment-type ${m.type}`}>{MOMENT_TYPES.find(t=>t.id===m.type)?.label}</span>
                            <div className="ma-moment-desc">{m.desc}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Notes */}
                    {viewing.coachNotes&&(
                      <div style={{padding:"0 1rem 1rem",borderTop:"1px solid var(--rule2)",marginTop:"0.5rem"}}>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.78rem",letterSpacing:"2px",color:"var(--muted)",margin:"0.65rem 0 0.4rem"}}>Coach Notes</div>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.8rem",color:"var(--ink2)",fontWeight:300,lineHeight:1.75,fontStyle:"italic"}}>{viewing.coachNotes}</div>
                      </div>
                    )}

                    {/* AI Report */}
                    <div style={{padding:"0 1rem 1rem",borderTop:"1px solid var(--rule2)",marginTop:"0.5rem"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"0.65rem 0 0.6rem"}}>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.82rem",letterSpacing:"2px",color:"var(--ink)"}}>
                          AI Match Report <span className="ma-ai-badge">Claude</span>
                        </div>
                        <button onClick={()=>genAI(viewing)} disabled={aiStreaming}
                          style={{padding:"0.38rem 0.85rem",background:"linear-gradient(135deg,var(--teal2),var(--teal))",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.65rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer",opacity:aiStreaming?0.6:1}}>
                          {aiStreaming?"Writing...":"✦ Generate Report"}
                        </button>
                      </div>
                      {aiStreaming&&<div className="ma-loading"><div className="ma-dot"/><div className="ma-dot"/><div className="ma-dot"/><span>Writing your match report...</span></div>}
                      {(aiOutput||viewing.aiOutput)&&(
                        <div className="ma-ai-out">{aiOutput||viewing.aiOutput}{aiStreaming&&<span className="ma-cursor"/>}</div>
                      )}
                      {!aiOutput&&!viewing.aiOutput&&!aiStreaming&&(
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic"}}>Click Generate Report for a Claude-written analysis of this match.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* List */}
                <div className="ma-panel">
                  <div className="ma-panel-head">
                    <div className="ma-panel-title">All Matches</div>
                    <span className="ma-chip teal">{analyses.length}</span>
                  </div>
                  {analyses.map(a=>{
                    const r = resultLetter(a.matchData?.goalsFor,a.matchData?.goalsAgainst);
                    const mark = a.pressEfficiency>0?pressBM(a.pressEfficiency):null;
                    return(
                      <div key={a.id} className="ma-history-row" onClick={()=>{setViewingId(a.id===viewingId?null:a.id);setAiOutput("");}}>
                        <div className={`ma-res ${r}`}>{r==="Q"?"?":r}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:700,color:"var(--ink)"}}>vs {a.matchData?.opponent}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",marginTop:"0.05rem"}}>
                            {fmtDate(a.matchData?.date)}{a.matchData?.competition?` · ${a.matchData.competition}`:""}{a.matchData?.venue?` · ${a.matchData.venue==="home"?"H":"A"}`:""}{(a.matchData?.goalsFor!==undefined&&a.matchData?.goalsAgainst!==undefined)?` · ${a.matchData.goalsFor}–${a.matchData.goalsAgainst}`:""}
                          </div>
                        </div>
                        {a.pressEfficiency>0&&(
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div className="ma-press-val" style={{color:a.pressEfficiency>=75?"#16a34a":a.pressEfficiency>=65?"var(--teal)":"var(--mustard)"}}>{a.pressEfficiency}%</div>
                            {mark&&<span className={`ma-bm ${mark.cls}`} style={{fontSize:"0.5rem"}}>{mark.label}</span>}
                          </div>
                        )}
                        {a.moments?.length>0&&<span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)",flexShrink:0,marginLeft:"0.4rem"}}>{a.moments.length}×</span>}
                        {a.aiOutput&&<span style={{fontSize:"0.65rem",color:"var(--teal)",flexShrink:0}}>✦</span>}
                        <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic",flexShrink:0}}>{a.savedAt}</span>
                        <button onClick={e=>{e.stopPropagation();if(window.confirm(`Delete analysis for vs ${a.matchData?.opponent}?`)){const u=analyses.filter(x=>x.id!==a.id);setAnalyses(u);saveToStorage("va_analyses",u);if(viewingId===a.id)setViewingId(null);}}} style={{background:"transparent",border:"none",color:"var(--rule)",cursor:"pointer",fontSize:"0.72rem",padding:"0.2rem 0.4rem",flexShrink:0}}>✕</button>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}

        </div>
      </div>
    </>
  );
}
